import { ArrowRight, Search, Loader2, Container, Weight, Users, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NUEVO_CLIENTE } from './nuevo-cliente-dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { cn } from '@/lib/utils/cn';
import type { TradeLocation, TradeContainerType } from '../types/trade.types';

/**
 * Buscador de rutas.
 *
 * La forma es la de una búsqueda de vuelos porque es lo que es: origen,
 * destino, equipo, peso. Cuatro campos en una barra, no un formulario en una
 * columna — cotizar un flete es una búsqueda, no una carga de datos.
 *
 * Los tributos NO se piden acá. Cotizar el flete no necesita saber qué
 * productos son; el costo por producto se calcula después, con la proforma
 * cargada y la partida arancelaria de cada línea.
 */

export interface RouteSearch {
  originLocationId: string;
  destinationLocationId: string;
  containerTypeId: string;
  weightKg: string;
  /**
   * Cliente al que se le cotiza. Cadena vacía = **importación propia**: no
   * lleva margen a tercero ni cotización en PDF, el total es el costo real.
   */
  customerId: string;
}

/** Valor del select que representa una importación para uno mismo. */
export const OWN_IMPORT = '';

export interface RouteSearchBarProps {
  origins: TradeLocation[];
  destinations: TradeLocation[];
  containerTypes: TradeContainerType[];
  customers: Array<{ id: string; name: string }>;
  value: RouteSearch;
  onChange: (value: RouteSearch) => void;
  onSearch: () => void;
  searching: boolean;
  className?: string;
  /** Abre el alta rápida de cliente. Sin esto, la opción no se ofrece. */
  onNuevoCliente?: () => void;
}

export function RouteSearchBar({
  origins,
  destinations,
  containerTypes,
  customers,
  value,
  onChange,
  onSearch,
  searching,
  className,
  onNuevoCliente,
}: RouteSearchBarProps) {
  /**
   * El equipo elegido, con su límite de carga.
   *
   * `maxCargoKg` lo calcula el backend descontándole la tara al peso bruto del
   * equipo. Viene de ahí y no se calcula acá para que la tabla de taras exista en
   * un solo lugar: dos copias de un dato físico terminan desincronizadas.
   */
  const selectedEquipment = containerTypes.find((c) => c.id === value.containerTypeId);

  /**
   * Se avisa **antes** de mandar la consulta.
   *
   * El backend igual valida y rechaza —es la defensa real— pero descubrirlo recién
   * al apretar "Buscar rutas" hace perder el viaje. Con el aviso en el campo, el
   * usuario corrige antes.
   */
  const requestedKg = Number(value.weightKg);
  const overweight =
    selectedEquipment != null &&
    selectedEquipment.maxCargoKg > 0 &&
    Number.isFinite(requestedKg) &&
    requestedKg > selectedEquipment.maxCargoKg
      ? {
          maxCargoKg: selectedEquipment.maxCargoKg,
          code: selectedEquipment.code,
          containersNeeded: Math.ceil(requestedKg / selectedEquipment.maxCargoKg),
        }
      : null;

  const canSearch =
    value.originLocationId !== '' && value.destinationLocationId !== '' && overweight === null;

  const set = <K extends keyof RouteSearch>(key: K, next: RouteSearch[K]) =>
    onChange({ ...value, [key]: next });

  const locationOption = (l: TradeLocation) => ({
    value: l.id,
    label: l.countryCode ? `${l.name} (${l.countryCode})` : l.name,
  });

  return (
    <form
      className={cn('space-y-2', className)}
      onSubmit={(e) => {
        e.preventDefault();
        if (canSearch && !searching) onSearch();
      }}
    >
      {/* El cliente va arriba y separado: define si la cotización lleva margen
          y si se le manda un PDF, no es un filtro de la búsqueda. */}
      <div className="max-w-md">
        <SearchField label="Para quién es" icon={Users}>
          {/*
            La opción de crear va AL FINAL de la lista, no arriba.

            Arriba compite con lo que se hace el 90 % de las veces —elegir un
            cliente que ya existe— y se toca sin querer. Al final aparece justo
            cuando alguien buscó y no encontró, que es el momento en que se
            necesita.
          */}
          <SearchableSelect
            options={[
              { value: OWN_IMPORT, label: 'Para mí (importación propia)' },
              ...customers.map((c) => ({ value: c.id, label: c.name })),
              ...(onNuevoCliente
                ? [{ value: NUEVO_CLIENTE, label: '+ Registrar cliente nuevo' }]
                : []),
            ]}
            value={value.customerId}
            onValueChange={(v) => {
              if (v === NUEVO_CLIENTE) {
                // No se guarda el centinela como si fuera un cliente: se abre el
                // alta y el selector queda como estaba hasta que se cree.
                onNuevoCliente?.();
                return;
              }
              set('customerId', v);
            }}
            placeholder="Para mí (importación propia)"
            searchPlaceholder="Buscar cliente..."
            className="border-0 shadow-none focus:ring-0"
          />
        </SearchField>
      </div>

      <div className="grid gap-2 lg:grid-cols-[1fr_auto_1fr_180px_150px_auto] lg:items-stretch">
        <SearchField label="Origen">
          <SearchableSelect
            options={origins.map(locationOption)}
            value={value.originLocationId}
            onValueChange={(v) => set('originLocationId', v)}
            placeholder="Puerto de embarque"
            searchPlaceholder="Buscar puerto..."
            className="border-0 shadow-none focus:ring-0"
          />
        </SearchField>

        {/* La flecha solo existe en escritorio: en móvil los campos van uno debajo del otro. */}
        <div className="hidden self-center lg:block" aria-hidden>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>

        <SearchField label="Destino">
          <SearchableSelect
            options={destinations.map(locationOption)}
            value={value.destinationLocationId}
            onValueChange={(v) => set('destinationLocationId', v)}
            placeholder="Ciudad de entrega"
            searchPlaceholder="Buscar destino..."
            className="border-0 shadow-none focus:ring-0"
          />
        </SearchField>

        <SearchField label="Equipo" icon={Container}>
          <SearchableSelect
            options={containerTypes.map((c) => ({ value: c.id, label: c.code }))}
            value={value.containerTypeId}
            onValueChange={(v) => set('containerTypeId', v)}
            placeholder="Contenedor"
            searchPlaceholder="Buscar..."
            className="border-0 shadow-none focus:ring-0"
          />
        </SearchField>

        {/*
          "Peso de la carga", no "peso bruto".

          En logística el peso bruto incluye el contenedor vacío, y lo que el
          usuario informa es su mercadería. El label anterior invitaba a poner el
          número equivocado — y de ahí salían cotizaciones con carga imposible.
        */}
        <SearchField label="Peso de la carga" icon={Weight}>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={selectedEquipment?.maxCargoKg || undefined}
            placeholder="18500 kg"
            value={value.weightKg}
            onChange={(e) => set('weightKg', e.target.value)}
            aria-invalid={overweight ? true : undefined}
            className={cn(
              'h-10 w-full bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground',
              overweight && 'text-bloqueo'
            )}
          />
        </SearchField>

        <Button
          type="submit"
          disabled={!canSearch || searching}
          variant="degradado"
          className="h-12 gap-1.5 lg:h-auto lg:self-stretch lg:px-6"
        >
          {searching ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Buscando
            </>
          ) : (
            <>
              <Search className="h-4 w-4" />
              Buscar rutas
            </>
          )}
        </Button>
      </div>

      {/*
        El aviso de sobrepeso, con la cuenta hecha.

        No alcanza con decir "no entra": el vendedor necesita saber cuántos
        contenedores harían falta, que es lo que el cliente le va a preguntar
        enseguida.
      */}
      {overweight && (
        <p className="mt-2 flex items-start gap-1.5 rounded-xl border border-bloqueo-borde bg-bloqueo-suave px-3 py-2 text-sm text-bloqueo">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <strong>
              Un {overweight.code} soporta {overweight.maxCargoKg.toLocaleString('es-BO')} kg de
              carga.
            </strong>{' '}
            Con {requestedKg.toLocaleString('es-BO')} kg harían falta{' '}
            <strong>{overweight.containersNeeded} contenedores</strong> — cotizá uno y multiplicá, o
            partí el embarque.
          </span>
        </p>
      )}
    </form>
  );
}

/** Un campo de la barra. La etiqueta chica arriba, el control debajo, sin bordes. */
function SearchField({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon?: typeof Container;
  children: React.ReactNode;
}) {
  return (
    <label className="block rounded-xl border bg-campo px-1 transition-[border-color,box-shadow] focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
      <span className="flex items-center gap-1 px-2 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-2.5 w-2.5" />}
        {label}
      </span>
      {children}
    </label>
  );
}
